import {
  type BillingDraftStatus,
  type BillingItemKind,
  type BillingPaymentSource,
  type BillingSettlementState,
  billingSettlementState,
  type KsefStatusKind,
} from '../contracts/billing';
import type { StatusTone } from '../contracts/common';

/** Statusy zestawienia (MVP §7: roboczy, czeka na akceptację, wystawiony, błąd, anulowany; Figma 09). */
export const BILLING_DRAFT_STATUS_LABELS: Record<BillingDraftStatus, string> = {
  draft: 'Roboczy',
  pending_approval: 'Czeka na akceptację',
  issued: 'Wystawiony',
  error: 'Błąd',
  cancelled: 'Anulowany',
};

export const BILLING_DRAFT_STATUS_TONES: Record<BillingDraftStatus, StatusTone> = {
  draft: 'neutral',
  pending_approval: 'accent',
  issued: 'warning',
  error: 'danger',
  cancelled: 'neutral',
};

/**
 * Etykieta statusu na liście (Figma 09: „Wystawiona, nieopłacona”, „Opłacona”): wystawione rozróżniamy po
 * opłaceniu; pozostałe statusy = `BILLING_DRAFT_STATUS_LABELS`.
 */
export function billingDraftDisplayStatus(
  d: { status: BillingDraftStatus; paidAt: Date | string | null },
  opts: { settlement?: boolean } = {},
): {
  label: string;
  tone: StatusTone;
} {
  if (opts.settlement) {
    const state = billingSettlementState(d);
    return { label: BILLING_SETTLEMENT_STATE_LABELS[state], tone: BILLING_SETTLEMENT_STATE_TONES[state] };
  }
  if (d.status === 'issued') {
    return d.paidAt ? { label: 'Opłacona', tone: 'success' } : { label: 'Wystawiona, nieopłacona', tone: 'warning' };
  }
  return { label: BILLING_DRAFT_STATUS_LABELS[d.status], tone: BILLING_DRAFT_STATUS_TONES[d.status] };
}

/**
 * Rozliczenia z klientem (workspace bez systemu faktur, DECISIONS 2026-10-09): Szkic → Wysłane do klienta → Opłacone;
 * Anulowane. „Czeka na akceptację” = oznaczenie jako wysłane zaproponowane przez AI / konektor albo przez osobę bez
 * „Wystawia faktury”.
 */
export const BILLING_SETTLEMENT_STATE_LABELS: Record<BillingSettlementState, string> = {
  draft: 'Szkic',
  pending: 'Czeka na akceptację',
  sent: 'Wysłane do klienta',
  paid: 'Opłacone',
  cancelled: 'Anulowane',
  error: 'Błąd',
};

export const BILLING_SETTLEMENT_STATE_TONES: Record<BillingSettlementState, StatusTone> = {
  draft: 'neutral',
  pending: 'accent',
  sent: 'warning',
  paid: 'success',
  cancelled: 'neutral',
  error: 'danger',
};

export const BILLING_ITEM_KIND_LABELS: Record<BillingItemKind, string> = {
  time: 'Czas',
  fixed: 'Pozycja stała',
};

export const BILLING_PAYMENT_SOURCE_LABELS: Record<BillingPaymentSource, string> = {
  manual: 'Oznaczone ręcznie',
  integration: 'Z systemu faktur',
};

const MONTHS_NOMINATIVE = [
  'styczeń',
  'luty',
  'marzec',
  'kwiecień',
  'maj',
  'czerwiec',
  'lipiec',
  'sierpień',
  'wrzesień',
  'październik',
  'listopad',
  'grudzień',
] as const;

function parts(date: string): { y: number; m: number; d: number } {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return { y, m, d };
}

function lastDayOfMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** Nazwa miesiąca: `billingMonthName('2026-09-01')` → „wrzesień”. */
export function billingMonthName(date: string): string {
  return MONTHS_NOMINATIVE[parts(date).m - 1] ?? '';
}

/**
 * Okres zestawienia na liście (Figma 09: „wrzesień 2026”): pełny miesiąc = nazwa miesiąca i rok, kilka pełnych
 * miesięcy = „sierpień–wrzesień 2026”, inaczej zakres dat („1–15.09.2026”, „15.08–14.09.2026”).
 */
export function billingPeriodLabel(from: string, to: string): string {
  const a = parts(from);
  const b = parts(to);
  const fullMonths = a.d === 1 && b.d === lastDayOfMonth(b.y, b.m);
  if (fullMonths) {
    if (a.y === b.y && a.m === b.m) return `${MONTHS_NOMINATIVE[a.m - 1]} ${a.y}`;
    if (a.y === b.y) return `${MONTHS_NOMINATIVE[a.m - 1]}–${MONTHS_NOMINATIVE[b.m - 1]} ${b.y}`;
    return `${MONTHS_NOMINATIVE[a.m - 1]} ${a.y}–${MONTHS_NOMINATIVE[b.m - 1]} ${b.y}`;
  }
  return billingPeriodRangeText(from, to);
}

/** Zakres dat okresu (Figma 09 Podsumowanie: „1–30.09.2026”). */
export function billingPeriodRangeText(from: string, to: string): string {
  const a = parts(from);
  const b = parts(to);
  const mm = (n: number) => String(n).padStart(2, '0');
  if (from === to) return `${a.d}.${mm(a.m)}.${a.y}`;
  if (a.y === b.y && a.m === b.m) return `${a.d}–${b.d}.${mm(b.m)}.${b.y}`;
  if (a.y === b.y) return `${a.d}.${mm(a.m)}–${b.d}.${mm(b.m)}.${b.y}`;
  return `${a.d}.${mm(a.m)}.${a.y}–${b.d}.${mm(b.m)}.${b.y}`;
}

/** Komunikat symulacji przy wystawionej fakturze (adapter fake). */
export const BILLING_SIMULATION_NOTE = 'Symulacja — faktura nie została wystawiona w systemie zewnętrznym';

/** Stan faktury w KSeF podany przez system faktur (Fravity OS nie wysyła do KSeF). */
export const KSEF_STATUS_LABELS: Record<KsefStatusKind, string> = {
  ok: 'Przyjęta w KSeF',
  pending: 'Wysyłana do KSeF',
  error: 'Błąd wysyłki do KSeF',
  offline: 'KSeF: tryb offline',
  not_applicable: 'Nie dotyczy KSeF',
  not_connected: 'KSeF niepodłączony w systemie faktur',
  unknown: 'KSeF: stan nieznany',
};

export const KSEF_STATUS_TONES: Record<KsefStatusKind, StatusTone> = {
  ok: 'success',
  pending: 'accent',
  error: 'danger',
  offline: 'warning',
  not_applicable: 'neutral',
  not_connected: 'warning',
  unknown: 'neutral',
};

/** Faktura oznaczona jako wystawiona ręcznie (numer przepisany z systemu faktur po ręcznej weryfikacji). */
export const BILLING_MARKED_ISSUED_NOTE = 'Numer przepisany ręcznie z systemu faktur po weryfikacji';
